// =============================================================================
// Admin Analytics — ServerDrilldownDialog
// =============================================================================
// The raw-data drill-down behind the Reports-tab widgets. The browser only ever
// holds the page on screen: the owning widget runs the paginated rows query and
// the (page-independent) facets query from the `ServerDrilldown` state, and this
// dialog renders both and feeds selections/date range/page changes back through
// the same state. The learner and provider filters list only their top values
// and search the rest on the server. Export CSV fetches every page of the
// current result under the hood (`useDrilldownExport`) and exports all of it,
// while the table keeps showing one page.
// =============================================================================
import { Column } from "@fronterahealth/frontera-ui-components";

import { CellErrorState } from "@pages/AdminAnalytics/components/CellErrorState";
import {
  FacetField,
  FacetOption,
  FacetedFilterBar,
  FilterValues,
} from "@pages/AdminAnalytics/components/DrilldownFilters";
import { DrilldownLayout } from "@pages/AdminAnalytics/components/DrilldownLayout";
import { FacetLabels, ServerDrilldown } from "@pages/AdminAnalytics/components/useServerDrilldown";
import {
  FACET_LIMIT,
  FacetPayload,
  FacetScope,
  SEARCHABLE_FACET_KEYS,
  facetLabel,
  toFacetOptions,
  truncatedFacetKeys,
} from "@pages/AdminAnalytics/data/analyticsFilters";
import { DrilldownResult } from "@pages/AdminAnalytics/data/hooks";
import { DrilldownExportState } from "@pages/AdminAnalytics/data/useDrilldownExport";
import { exportRowsToCsv } from "@pages/AdminAnalytics/helpers";

/** One CSV column: header + cell text. */
export interface ExportField<T> {
  label: string;
  get: (row: T) => string;
}

interface ServerDrilldownDialogProps<T> {
  title: string;
  drilldown: ServerDrilldown;
  /** The rows page and (separately fetched) filter options, from a `use*Drilldown` hook. */
  result: DrilldownResult<{ totalCount?: number; rows: T[] }, readonly FacetPayload[]>;
  columns: Column<T>[];
  /** Which backend facets to offer as filters, in display order. */
  facetFields: FacetField[];
  /** Per facet key, the values to keep (see `toFacetOptions`). */
  facetScope?: FacetScope;
  exportFields: ExportField<T>[];
  exportFileName: string;
  onRowClick?: (row: T) => void;
}

const rowSummary = (totalCount: number) => `${totalCount} ${totalCount === 1 ? "row" : "rows"}`;

/** Progress while an export runs, or its failure with a retry. */
const exportStatusFor = (state: DrilldownExportState, retry: () => void) => {
  if (state.status === "exporting") {
    return state.total === undefined
      ? "Exporting…"
      : `Exporting… ${state.fetched.toLocaleString()} of ${state.total.toLocaleString()} rows`;
  }
  if (state.status === "error") {
    return (
      <>
        Couldn&apos;t export these rows.{" "}
        <button type="button" onClick={retry} className="font-medium text-text-primary hover:underline">
          Try again
        </button>
      </>
    );
  }
  return undefined;
};

/** Labels the options on screen give the selected values, to remember them by. */
const labelsOf = (selections: FilterValues, options: Record<string, FacetOption[]>): FacetLabels => {
  const labels: FacetLabels = {};
  Object.entries(selections).forEach(([key, values]) => {
    values.forEach((value) => {
      const label = options[key]?.find((o) => o.value === value)?.label;
      if (label) labels[key] = { ...labels[key], [value]: label };
    });
  });
  return labels;
};

/** No row count while loading or after a failure — "0 rows" would read as an empty result. */
const summaryFor = (result: { isLoading: boolean; isError: boolean }, totalCount: number) => {
  if (result.isLoading) return "Loading…";
  if (result.isError) return undefined;
  return rowSummary(totalCount);
};

export const ServerDrilldownDialog = <T,>({
  title,
  drilldown,
  result,
  columns,
  facetFields,
  facetScope,
  exportFields,
  exportFileName,
  onRowClick,
}: ServerDrilldownDialogProps<T>) => {
  const rows = result.page?.rows ?? [];
  const totalCount = result.page?.totalCount ?? 0;
  const options = toFacetOptions(result.facets, facetScope);
  // A selected value's label survives the options reloading or dropping it.
  const valueLabel = (key: string, value: string) => drilldown.knownLabels[key]?.[value] ?? facetLabel(key, value);
  const onSelectionsChange = (selections: FilterValues) =>
    drilldown.setSelections(selections, labelsOf(selections, options));
  const exportAll = async () => {
    const all = await result.export.run();
    if (all) exportRowsToCsv(exportFileName, exportFields, all);
  };
  // Chips that changed in the last moment haven't reached the queries yet; the
  // export would describe the previous selection.
  const selectionsPending = drilldown.selections !== drilldown.query.selections;
  return (
    <DrilldownLayout
      title={title}
      open={drilldown.open}
      setOpen={drilldown.setOpen}
      filterBar={
        <FacetedFilterBar
          fields={facetFields}
          options={options}
          valueLabel={valueLabel}
          values={drilldown.selections}
          onChange={onSelectionsChange}
          dateValue={drilldown.dateRange}
          onDateChange={drilldown.setDateRange}
          searchKeys={SEARCHABLE_FACET_KEYS}
          onSearchChange={drilldown.setFacetSearch}
          truncatedKeys={truncatedFacetKeys(result.facets)}
          optionLimit={FACET_LIMIT}
        />
      }
      filterNotice={
        result.facetsError ? (
          <p className="text-xs text-text-secondary">
            Filter options couldn&apos;t load.{" "}
            <button
              type="button"
              onClick={result.refetchFacets}
              className="font-medium text-text-primary hover:underline"
            >
              Try again
            </button>
          </p>
        ) : undefined
      }
      error={
        result.isError ? <CellErrorState message="Couldn't load these rows." onRetry={result.refetch} /> : undefined
      }
      summary={summaryFor(result, totalCount)}
      onExport={() => void exportAll()}
      // While rows load (or new chips settle) the count on screen is the previous result's;
      // never start an export of it. One export at a time.
      exportDisabled={
        rows.length === 0 || result.isFetching || selectionsPending || result.export.state.status === "exporting"
      }
      exportStatus={exportStatusFor(result.export.state, () => void exportAll())}
      rows={rows}
      columns={columns}
      loading={result.isFetching}
      totalItemCount={totalCount}
      page={drilldown.page.page}
      pageSize={drilldown.page.pageSize}
      onPageChange={drilldown.setPage}
      onRowClick={onRowClick}
    />
  );
};
